/**
 * #1897 / F-N06 — facility scope hydration model.
 *
 * Viewer facility_ids must not default to empty-as-corporate. Scope is
 * unknown until authoritative assignments/list resolve, then one of:
 * - none: authenticated but zero facilities
 * - concrete: explicit facility id list
 * - tenant_wide: admin/superadmin tenant list (all tenant facilities)
 */

export type FacilityScopeStatus = 'unknown' | 'none' | 'concrete' | 'tenant_wide'

export type FacilityScope = {
  facility_ids: string[]
  facility_scope_status: FacilityScopeStatus
}

export type FacilityScopeRole = 'Staff' | 'Surveyor' | 'Admin' | 'Reviewer' | 'FieldSurveyor'

/**
 * Derive scope status from loaded facility ids and role.
 * Empty list after load is always `none` (fail closed), never corporate-by-default.
 */
export function deriveFacilityScope(
  facilityIds: string[],
  role: FacilityScopeRole,
  isSuperadmin: boolean,
): FacilityScope {
  const ids = Array.from(
    new Set(
      facilityIds
        .filter((id): id is string => typeof id === 'string' && id.trim().length > 0)
        .map((id) => id.trim()),
    ),
  )
  if (ids.length === 0) {
    return { facility_ids: [], facility_scope_status: 'none' }
  }
  // Admins with multi-facility tenant list are tenant_wide for UX heuristics;
  // ids remain the authoritative concrete set for scoping.
  if ((role === 'Admin' || isSuperadmin) && ids.length > 1) {
    return { facility_ids: ids, facility_scope_status: 'tenant_wide' }
  }
  return { facility_ids: ids, facility_scope_status: 'concrete' }
}

/** Unknown scope — used until hydration completes. */
export function unknownFacilityScope(): FacilityScope {
  return { facility_ids: [], facility_scope_status: 'unknown' }
}

/**
 * Fail closed: product paths that require a facility must not proceed
 * when scope is still unknown or resolved empty.
 */
export function requiresFacilityContext(scope: FacilityScope | null | undefined): boolean {
  if (!scope) return true
  return scope.facility_scope_status === 'unknown' || scope.facility_scope_status === 'none'
}

export function hasConcreteFacility(scope: FacilityScope | null | undefined): boolean {
  if (!scope) return false
  return (
    (scope.facility_scope_status === 'concrete' || scope.facility_scope_status === 'tenant_wide') &&
    scope.facility_ids.length > 0
  )
}

/**
 * Multi-facility UX heuristic (portfolio / corporate shells).
 * Fail closed: enterprise with unknown/none scope is NOT multi (empty ≠ corporate).
 * Consultant remains multi-tenant-by-tier after auth.
 */
export function isMultiFacilityViewer(viewer: {
  tier: string
  facility_ids: string[]
  facility_scope_status: FacilityScopeStatus
} | null | undefined): boolean {
  if (!viewer) return false
  if (viewer.tier === 'consultant') return true
  if (viewer.tier !== 'enterprise') return false
  if (viewer.facility_scope_status === 'unknown' || viewer.facility_scope_status === 'none') {
    return false
  }
  return viewer.facility_ids.length !== 1
}
